/**
 * SocialProofTicker — V141.5.
 *
 * Rotating banner showing anonymized recent workspace activations.
 * Each item: "K. z Bratislavy aktivoval Helpdesk Agent · pred 12 min".
 *
 * Visual mode:
 *   • Default: subtle white text on dark surface, fades between items every 8s
 *   • Trending mode (when momentum.is_trending=true): red pulse + 🔥 prefix +
 *     "Trending — X trialov za 24h" wrapper item interleaved every 3rd rotation
 *
 * Polls /api/social-proof/recent every 30s. If the endpoint fails or
 * returns empty (e.g. first 24h after launch with 0 signups), the
 * component renders nothing — never show fake/seeded data.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Flame, Sparkles } from "lucide-react";

const ROTATE_MS = 8_000;
const POLL_MS = 30_000;

export default function SocialProofTicker() {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const rotation = useRef(0);

  // Polling — fetch + refresh
  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const r = await axios.get(`${API}/activations/recent?limit=10`);
        if (!cancelled) setData(r.data);
      } catch (e) { /* silent — ticker hides itself */ }
    };
    fetch();
    const t = setInterval(fetch, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Rotation
  useEffect(() => {
    if (!data?.items?.length) return undefined;
    const t = setInterval(() => {
      rotation.current = (rotation.current + 1) % 1000;
      setIdx((i) => (i + 1) % data.items.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [data]);

  if (!data?.items?.length) return null;

  const trending = data.momentum?.is_trending;
  // Every 3rd rotation, show the momentum wrapper if trending.
  const showTrendingCard = trending && rotation.current % 3 === 2;

  const item = data.items[idx];
  return (
    <div
      data-testid="social-proof-ticker"
      className={`relative px-4 py-2 border ${
        trending
          ? "border-red-500/60 bg-red-950/30 animate-pulse"
          : "border-gray-800 bg-neutral-950/80"
      } overflow-hidden`}
    >
      <div className="flex items-center gap-2 text-xs sm:text-sm">
        {showTrendingCard ? (
          <>
            <Flame className="w-4 h-4 text-red-400 shrink-0" />
            <span className="font-black text-red-300 uppercase tracking-widest text-[11px]">
              Trending · {data.momentum.last_24h} trialov za 24h
            </span>
            <span className="text-[11px] text-red-200 ml-auto">
              {data.momentum.ratio}× nad priemerom
            </span>
          </>
        ) : (
          <>
            <span className="relative flex h-2 w-2 shrink-0">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${trending ? "bg-red-400" : "bg-emerald-400"}`} />
              <span className={`relative inline-flex rounded-full h-2 w-2 ${trending ? "bg-red-500" : "bg-emerald-500"}`} />
            </span>
            <span className="text-gray-200 truncate flex-1" data-testid="social-proof-current">
              {item.label}
            </span>
            <span className="text-[11px] text-gray-500 shrink-0">{item.age}</span>
          </>
        )}
      </div>
      {data.total_trials_this_week >= 5 && (
        <p className="text-[10px] text-gray-500 mt-1 uppercase tracking-widest font-black inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Tento týždeň · {data.total_trials_this_week} trialov spustených
        </p>
      )}
    </div>
  );
}
